import { RadioGroup } from '@mui/material';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { z } from 'zod';
import type { VoidFunctionComponent } from 'react';

import { OIDCProviderScope } from '../../../../../shared/model-typings/oidcprovider';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import useCreateOIDCProvider, {
  type CreateOIDCProviderRequest,
} from '../../../../hooks/api/oidc/useCreateOIDCProvider';
import useTeams from '../../../../hooks/api/teams/useTeams';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import { ErrorAlert } from '../../../generic';
import Button, { ButtonGroup, LoaderButton } from '../../../generic/Button';
import FormLabel from '../../../generic/Forms/FormLabel';
import LabeledRadio from '../../../generic/Forms/LabeledRadio';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import { Select } from '../../../generic/Select';
import type { SelectOption } from '../../../../types';

import styles from './OIDCProviders.module.less';

type ModalCreateOIDCProviderProps = {
  isOpen: boolean;
  onHide: () => void;
  teamId?: number;
};

const validateUrl = (url: string): boolean => {
  try {
    z.url({ protocol: /^https$/, hostname: z.regexes.domain, normalize: true }).parse(url);
    return true;
  } catch {
    return false;
  }
};

const ModalCreateOIDCProvider: VoidFunctionComponent<ModalCreateOIDCProviderProps> = ({
  isOpen,
  onHide,
  teamId,
}) => {
  const hasPermission = useHasPermission();
  const canManageAllProviders = hasPermission({
    action: Action.Manage,
    resourceType: ResourceType.OrganizationOIDCProvider,
  });
  const [issuerUrl, setIssuerUrl] = useState('');
  const [issuerUrlError, setIssuerUrlError] = useState<string | null>(null);
  const [selectedScope, setSelectedScope] = useState<OIDCProviderScope>(OIDCProviderScope.ORG);
  const [selectedTeam, setSelectedTeam] = useState<SelectOption<number> | null>(null);

  // If teamId is provided, we're in team context
  const isTeamContext = teamId !== undefined;

  const createProviderMutation = useCreateOIDCProvider();
  const { data: teams, isLoading: isLoadingTeams } = useTeams(!isTeamContext);

  // Convert teams to select options
  const teamOptions: Array<SelectOption<number>> = (teams || [])
    .filter((team) => {
      return canManageAllProviders || hasPermission({
        action: Action.Manage,
        resourceType: ResourceType.TeamOIDCProvider,
        teamId: team.id,
      });
    })
    .map((team) => ({
      value: team.id,
      label: team.name,
    }));

  // Default to team scope if user doesn't have org permission
  useEffect(() => {
    if (!canManageAllProviders) {
      setSelectedScope(OIDCProviderScope.TEAM);
    }
  }, [canManageAllProviders]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIssuerUrl('');
      setIssuerUrlError(null);
      setSelectedScope(canManageAllProviders ? OIDCProviderScope.ORG : OIDCProviderScope.TEAM);
      setSelectedTeam(null);
    }
  }, [isOpen, canManageAllProviders]);

  const handleUrlBlur = () => {
    if (issuerUrl && !validateUrl(issuerUrl)) {
      setIssuerUrlError('Please enter a valid URL (e.g., https://example.com)');
    } else {
      setIssuerUrlError(null);
    }
  };

  const handleSubmit = async () => {
    // Validate issuer URL
    if (!issuerUrl.trim()) {
      setIssuerUrlError('Issuer URL is required');
      return;
    }

    if (!validateUrl(issuerUrl)) {
      setIssuerUrlError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    let request: CreateOIDCProviderRequest;

    // Team context: use provided teamId
    if (isTeamContext && teamId !== undefined) {
      request = {
        issuer: issuerUrl.trim(),
        scope: OIDCProviderScope.TEAM,
        scopeId: teamId,
      } satisfies CreateOIDCProviderRequest;
    } else if (selectedScope === OIDCProviderScope.TEAM && selectedTeam) {
      // Org context with team selected
      request = {
        issuer: issuerUrl.trim(),
        scope: OIDCProviderScope.TEAM,
        scopeId: selectedTeam.value,
      } satisfies CreateOIDCProviderRequest;
    } else if (selectedScope === OIDCProviderScope.ORG) {
      // Org context with org scope
      request = {
        issuer: issuerUrl.trim(),
        scope: OIDCProviderScope.ORG,
      } satisfies CreateOIDCProviderRequest;
    } else {
      return;
    }

    try {
      await createProviderMutation.mutateAsync(request);
      onHide();
    } catch (err) {
      // Error is captured in mutation state and displayed below
    }
  };

  const shouldShowTeamDropdown = !canManageAllProviders || selectedScope === OIDCProviderScope.TEAM;
  const isSubmitDisabled =
    createProviderMutation.isPending ||
    !!issuerUrlError ||
    !issuerUrl.trim() ||
    (!isTeamContext && shouldShowTeamDropdown && !selectedTeam);

  const apiErrors = createProviderMutation.isError
    ? errorMessagesListFromResponse(createProviderMutation.error)
    : null;

  return (
    <Modal show={isOpen} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Create a new OIDC Provider</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiErrors && <ErrorAlert status={{ errors: apiErrors }} />}

        <LabeledTextInput
          labelVariant="legacy"
          label="Issuer URL"
          isRequired
          type="text"
          placeholder="https://example.com"
          value={issuerUrl}
          onChange={(newURL) => setIssuerUrl(newURL)}
          isDisabled={createProviderMutation.isPending}
          onBlur={handleUrlBlur}
        />
        {issuerUrlError && <span className={styles.error}>{issuerUrlError}</span>}

        {!isTeamContext && (
          <>
            {canManageAllProviders && (
              <FormLabel variant="legacy" label="Scope">
                <RadioGroup
                  value={selectedScope}
                  onChange={(e) => setSelectedScope(e.target.value as OIDCProviderScope)}
                >
                  <LabeledRadio
                    label="Organization"
                    value={OIDCProviderScope.ORG}
                    isDisabled={createProviderMutation.isPending}
                  />
                  <LabeledRadio
                    label="Team"
                    value={OIDCProviderScope.TEAM}
                    isDisabled={createProviderMutation.isPending}
                  />
                </RadioGroup>
              </FormLabel>
            )}

            {shouldShowTeamDropdown && (
              <FormLabel variant="legacy" label="Team">
                <Select
                  id="select-oidcprovider-team"
                  value={selectedTeam}
                  onChange={(option) => setSelectedTeam(option)}
                  options={teamOptions}
                  placeholder="Choose a team..."
                  isLoading={isLoadingTeams}
                  isDisabled={createProviderMutation.isPending}
                  isClearable={false}
                />
              </FormLabel>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={onHide} isDisabled={createProviderMutation.isPending}>
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            onClick={handleSubmit}
            isLoading={createProviderMutation.isPending}
            isDisabled={isSubmitDisabled}
          >
            Create Provider
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalCreateOIDCProvider;
